import Accordion from '@web/components/Accordion'
import ProductName from '@web/components/ProductName'
import { Link } from 'react-router-dom'

type QA = { q: string; a: React.ReactNode }

type Group = {
  heading: string
  items: QA[]
}

const groups: Group[] = [
  {
    heading: 'Buying and contracting',
    items: [
      {
        q: 'Is Resilience Explorer® | Property included if we already have Resilience Explorer®?',
        a: (
          <p className="text-base text-brand-navy/90 leading-relaxed m-0">
            Yes, if you are on the Professional or Enterprise tier. <ProductName /> is a feature of
            those tiers, not a separate purchase. Standard-tier clients can upgrade mid-term and we
            pro-rata the difference. Talk to your Customer Success contact about activation.
          </p>
        ),
      },
      {
        q: 'What happens to our data and outputs if we end the contract?',
        a: (
          <p className="text-base text-brand-navy/90 leading-relaxed m-0">
            All your data and all generated reports are exported to you in a standard format on
            contract end. No lock-in. No archival fees. This is in the master service agreement.
          </p>
        ),
      },
    ],
  },
  {
    heading: 'Implementation and IT',
    items: [
      {
        q: 'How quickly can Resilience Explorer® | Property be live for our council?',
        a: (
          <p className="text-base text-brand-navy/90 leading-relaxed m-0">
            Four to eight weeks from a signed agreement. Data integration with your council hazard
            layers is the variable. Hamilton went live in six weeks.
          </p>
        ),
      },
      {
        q: 'What does our team need to provide?',
        a: (
          <p className="text-base text-brand-navy/90 leading-relaxed m-0">
            Property addresses from your rating database, and access to your hazard data. We handle
            integration, configuration, and training of your LIM and consent staff.
          </p>
        ),
      },
    ],
  },
  {
    heading: 'Methodology and scope',
    items: [
      {
        q: 'How does Resilience Explorer® | Property handle hazard data that is missing or incomplete?',
        a: (
          <p className="text-base text-brand-navy/90 leading-relaxed m-0">
            The report shows &ldquo;Insufficient Data&rdquo; classifications where council data is
            missing. This is deliberate and aligns with NPS-NH Policy 5: decisions are made on the
            best available information, and the gaps are visible rather than hidden. We work with
            you to fill the gaps over time.
          </p>
        ),
      },
      {
        q: 'What if our hazard models change, or our council updates its data?',
        a: (
          <p className="text-base text-brand-navy/90 leading-relaxed m-0">
            <ProductName /> re-runs assessments automatically when source data is updated. Each LIM
            and each NPS-NH report uses the most current data version available. The audit trail
            records which version was used on which date for which report.
          </p>
        ),
      },
      {
        q: 'Does Resilience Explorer® | Property replace our GIS team or our hazards consultants?',
        a: (
          <p className="text-base text-brand-navy/90 leading-relaxed m-0">
            No. <ProductName /> is the automation layer between your existing hazard data and your
            LIM and consent outputs. Your GIS team still owns the data. Your hazards consultants
            still own the modelling. <ProductName /> automates the compliance reporting that sits on
            top.
          </p>
        ),
      },
      {
        q: 'What does Resilience Explorer® | Property NOT do?',
        a: (
          <p className="text-base text-brand-navy/90 leading-relaxed m-0">
            <ProductName /> is a screening-level tool that produces compliance-grade reports for
            LIMs and consents. It does not replace site-specific geotechnical investigation. It does
            not assess specific subdivision or development proposals. It does not evaluate
            mitigation effectiveness or residual risk. These remain a planner&apos;s job, informed
            by its evidence base.
          </p>
        ),
      },
      {
        q: 'How are risk levels calculated?',
        a: (
          <p className="text-base text-brand-navy/90 leading-relaxed m-0">
            <ProductName /> applies the NPS-NH Appendix 1 risk matrix exactly as prescribed:
            likelihood from the hazard data, consequence from validated damage and life-safety
            functions, combined per the regulation. Full methodology is in the sample report
            (downloadable above) and in our{' '}
            <Link
              to="/products/property/methodology"
              className="text-brand-blue font-semibold no-underline hover:underline"
            >
              published technical documentation
            </Link>
            .
          </p>
        ),
      },
    ],
  },
]

const FAQ = () => {
  return (
    <section className="bg-white py-24 sm:py-16">
      <div className="space-y-12 mx-auto max-w-5xl px-6 lg:px-8">
        <div className="space-y-3">
          <span className="text-xs font-semibold uppercase tracking-widest text-brand-blue">
            Questions buyers ask
          </span>
          <h2 className="text-4xl font-light text-brand-blue m-0">
            The questions that come up before booking a walkthrough.
          </h2>
        </div>

        <div className="space-y-12 max-w-4xl">
          {groups.map((group) => (
            <div key={group.heading} className="space-y-4">
              <h3 className="text-xs font-semibold uppercase tracking-widest text-brand-navy m-0">
                {group.heading}
              </h3>
              <Accordion
                items={group.items.map((item) => ({
                  title: item.q,
                  content: <div>{item.a}</div>,
                }))}
              />
            </div>
          ))}
        </div>

        <p className="text-base text-brand-navy/80 m-0">
          For a technical buyer&apos;s view of how risk is calculated, see the{' '}
          <Link
            to="/products/property/methodology"
            className="text-brand-blue font-semibold no-underline hover:underline"
          >
            <ProductName /> methodology page →
          </Link>
        </p>
      </div>
    </section>
  )
}

export default FAQ
